import {
  Allergy,
  AllergyService,
  CardiacRiskFactorPayload,
  Emphasis,
  HemstopPayload,
  LeePayload,
  MedicalHo,
  MedicalHoItem,
  MedicalHoItemCategory,
  MedicalHoService,
  MedicalScore,
  MedicalScoreName,
  MedicalScoreService,
  SourceType,
  SurgicalEvent
} from '@/modules/surgicalEvent';
import {
  BasicScratchMhoSuggestion,
  ClearScratchMhoSuggestion,
  DrawerScratchMhoSuggestion,
  ScratchMhoSuggestionData,
  ScratchMhoSuggestionType,
  ScratchSuggestion
} from './scratchMhoSuggestion.model';
import { ScratchSuggestionHelper } from './scratchSuggestionHelper';
import { captureThrowOnDev } from '@/util';
import { SnomedService } from '@/modules/snomed/snomed.service';
import {
  createMhoAnswerCheck,
  getUnansweredMhoDefaults,
  MhoAnswer
} from './scratchMhoAnswers';

export type ScratchMhoSuggestionCategory = MedicalHoItemCategory | 'ALLERGIES';

export type MhoSuggestionsPerCategory = Partial<
  Record<ScratchMhoSuggestionCategory, ScratchSuggestion[]>
>;

export type PersistSuggestionPayload = {
  suggestion: ScratchSuggestion;
  activeSe: SurgicalEvent;
  category?: ScratchMhoSuggestionCategory;
  emphasis?: Emphasis;
  biChoiceChoice?: 'left' | 'right';
  drawerData?: Parameters<DrawerScratchMhoSuggestion['toRequestBody']>[0];
};

export type MasterClearPayload = {
  activeSe: SurgicalEvent | undefined;
  medicalHo: MedicalHo | undefined;
  map: MhoSuggestionsPerCategory;
};

export class ScratchMhoSuggestionService {
  private static readonly INSTANCE = new ScratchMhoSuggestionService();

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private constructor() {}

  public static getInstance() {
    return this.INSTANCE;
  }

  findDrawerSuggestionByQcode(qcode: string) {
    const s = ScratchSuggestionHelper.DrawerSuggestionsByQcode[qcode];
    if (!s) {
      throw new Error(`Drawer suggestion not found: ${qcode}`);
    }
    return s;
  }

  public cacheSnomeds(suggestionsMap: MhoSuggestionsPerCategory) {
    const snomedCodes = Object.values(suggestionsMap)
      .flat()
      .filter((s) => !!(s as BasicScratchMhoSuggestion).snomedCode)
      .map((s) => (s as BasicScratchMhoSuggestion).snomedCode) as string[];

    SnomedService.getInstance().cacheSnomedItems(snomedCodes);
  }

  public masterClear({
    activeSe,
    medicalHo,
    map
  }: MasterClearPayload): Promise<void> {
    if (!activeSe) {
      const err = new Error('Active surgical event is undefined');
      captureThrowOnDev(err);
      return Promise.reject(err);
    }

    if (!medicalHo) {
      const err = new Error('Medical ho is undefined');
      captureThrowOnDev(err);
      return Promise.reject(err);
    }

    const patientId = activeSe.patient.id;
    const medicalHoService = MedicalHoService.getInstance(patientId);
    const promises: Promise<unknown>[] = [];
    const answers = getExistingMhoAnswers(medicalHo);
    const mhos: Partial<MedicalHoItem>[] = [];
    const scores: Partial<MedicalScore>[] = [];

    if (!AllergyService.getInstance(patientId).isCleared()) {
      promises.push(
        this.addAllergy({ code: AllergyService.SC_CLEARED_CODE }, activeSe)
      );
    }

    for (const category of Object.values(MedicalHoItemCategory)) {
      const suggestions = map[category];
      if (
        !suggestions ||
        medicalHoService.isCleared(category) ||
        !MedicalHoService.isDisplayableForSurgicalEvent(category, activeSe)
      ) {
        continue;
      }
      const clear = suggestions.find(
        (suggestion): suggestion is ClearScratchMhoSuggestion =>
          suggestion.suggestionType === 'clear' &&
          suggestion.applies(activeSe) &&
          suggestion.id !== 'clear_obs_primipara'
      );
      if (!clear) {
        mhos.push({ category, code: MedicalHoService.SC_CLEARED_CODE });
        continue;
      }
      const payload = this.buildClearPayload(clear, category, activeSe, [
        ...answers,
        ...mhos
      ]);
      mhos.push(...payload.mhos);
      scores.push(...payload.scores);
    }

    if (mhos.length) {
      promises.push(medicalHoService.addItems(mhos));
    }
    if (scores.length) {
      promises.push(
        MedicalScoreService.getInstance(patientId).finalizeMultiple(scores)
      );
    }
    return Promise.all(promises).then(() => undefined);
  }

  public persistSuggestion(
    {
      suggestion,
      activeSe,
      biChoiceChoice,
      category,
      emphasis,
      drawerData
    }: PersistSuggestionPayload,
    medicalHo?: MedicalHo
  ): Promise<void> {
    if (!activeSe) {
      return Promise.reject(new Error('Active surgical event is undefined'));
    }
    if (category === 'ALLERGIES') {
      return this.addAllergy(suggestion, activeSe);
    }
    if (!medicalHo) {
      return Promise.reject(new Error('Medical ho is undefined'));
    }

    const answers = getExistingMhoAnswers(medicalHo);
    const suggestionType = suggestion.suggestionType;
    if (suggestionType === 'clear') {
      if (!category) {
        return Promise.reject(
          new Error('Medical history category is undefined')
        );
      }
      const { mhos, scores } = this.buildClearPayload(
        suggestion,
        category,
        activeSe,
        answers
      );
      return Promise.all([
        MedicalHoService.getInstance(activeSe.patient.id).addItems(mhos),
        scores.length
          ? MedicalScoreService.getInstance(
              activeSe.patient.id
            ).finalizeMultiple(scores)
          : Promise.resolve()
      ]).then(() => undefined);
    }

    let payload: ScratchMhoSuggestionData[];
    if (suggestionType === 'bi-choice') {
      if (!biChoiceChoice) {
        throw new Error('choice param should be provided');
      }
      payload = suggestion.toRequestBody(biChoiceChoice);
    } else if (suggestionType === 'drawer') {
      if (!drawerData) {
        throw new Error('drawerData param should be provided');
      }
      payload = suggestion.toRequestBody(drawerData);
    } else {
      payload = suggestion.toRequestBody();
    }

    const hasAnswer = createMhoAnswerCheck(answers);
    const mhos: Partial<MedicalHoItem>[] = [];
    const promises: Promise<unknown>[] = [];
    for (const data of payload) {
      if (data.category === 'ALLERGIES') {
        promises.push(this.addAllergy(data, activeSe));
      } else {
        const item = this.toMhoPayload(data, category, suggestionType);
        if (!hasAnswer(item)) {
          mhos.push(emphasis ? { ...item, emphasis } : item);
        }
      }
    }
    if (mhos.length) {
      promises.push(
        MedicalHoService.getInstance(activeSe.patient.id).addItems(mhos)
      );
    }
    return Promise.all(promises).then(() => undefined);
  }

  /** Category and global Clear use the same defaults and completion marker rules. */
  private buildClearPayload(
    suggestion: ClearScratchMhoSuggestion,
    category: MedicalHoItemCategory,
    activeSe: SurgicalEvent,
    answers: readonly MhoAnswer[]
  ): { mhos: Partial<MedicalHoItem>[]; scores: Partial<MedicalScore>[] } {
    const defaults =
      !suggestion.sticky &&
      answers.some((answer) => answer.category === category)
        ? []
        : suggestion
            .toRequestBody(activeSe)
            .map((data) => this.toMhoPayload(data, category, 'clear'));
    const mhos = getUnansweredMhoDefaults(defaults, answers);
    if (!mhos.some((item) => item.category === category)) {
      mhos.push({ category, code: MedicalHoService.SC_CLEARED_CODE });
    }
    const hasAnswer = createMhoAnswerCheck(answers);
    const scores = suggestion
      .getScoresToClear(activeSe)
      .filter(
        (name) =>
          !hasAnswer({
            qcode: MedicalHoService.getQcodeByScoreName(name),
            isScore: true
          })
      )
      .map((name) => ({ name, payload: this.getClearScorePayload(name) }));
    return { mhos, scores };
  }

  private getClearScorePayload(
    score: MedicalScoreName
  ): MedicalScore['payload'] {
    if (score === MedicalScoreName.LEE) {
      return {
        stroke: false,
        chronicKidneyFailure: false,
        heartFailure: false,
        diabetes: false,
        ischemicHeartDisease: false
      } as LeePayload;
    }

    if (score === MedicalScoreName.CARDIAC_RISK_FACTOR) {
      return {
        dyslipidemia: false,
        diabetes: false,
        highBloodPressure: false,
        smoker: false,
        vascularRiskFamily: false
      } as CardiacRiskFactorPayload;
    }

    if (score === MedicalScoreName.HEMSTOP) {
      return {
        persistentBleeding: false,
        frequentBruising: false,
        surgeryBleeding: false,
        dentalBleeding: false,
        postpartumBleeding: false,
        menorrhagia: false,
        familyBleedingDisorder: false
      } as HemstopPayload;
    }

    captureThrowOnDev(new Error(`Cannot clear score: ${score}`));
    return {};
  }

  private toMhoPayload(
    data: ScratchMhoSuggestionData,
    category: ScratchMhoSuggestionCategory | undefined,
    suggestionType: ScratchMhoSuggestionType
  ): Partial<MedicalHoItem> {
    if (data.category === 'ALLERGIES' || category === 'ALLERGIES') {
      throw new Error('Allergy data detected in mho payload');
    }
    return {
      ...data,
      category: ScratchSuggestionHelper.getQuestionnaireCategory(
        data,
        data.category ?? category
      ),
      scratchConsultationCleared:
        suggestionType === 'clear' &&
        data.code !== MedicalHoService.SC_CLEARED_CODE
    };
  }

  private addAllergy(
    s: ScratchSuggestion | ScratchMhoSuggestionData,
    activeSe: SurgicalEvent
  ): Promise<void> {
    const arr =
      'suggestionType' in s
        ? s.suggestionType === 'clear'
          ? s.toRequestBody(activeSe)
          : s.toRequestBody()
        : [s];
    return Promise.all(
      arr.map(({ code, notes }) =>
        AllergyService.getInstance(activeSe.patient.id).upsert({
          substanceCode: code,
          sourceType: SourceType.PREDEFINED,
          notes
        })
      )
    ).then();
  }
}

export function filterCategorySuggestions(
  map: MhoSuggestionsPerCategory,
  category: 'ALLERGIES',
  payload: Allergy[] | undefined,
  activeSe?: SurgicalEvent
): ScratchSuggestion[] | undefined;
export function filterCategorySuggestions(
  map: MhoSuggestionsPerCategory,
  category: Exclude<ScratchMhoSuggestionCategory, 'ALLERGIES'>,
  payload: MedicalHo | undefined,
  activeSe?: SurgicalEvent
): ScratchSuggestion[] | undefined;
export function filterCategorySuggestions(
  map: MhoSuggestionsPerCategory,
  category: ScratchMhoSuggestionCategory,
  payload: MedicalHo | Allergy[] | undefined,
  activeSe?: SurgicalEvent
): ScratchSuggestion[] | undefined {
  if (category === 'ALLERGIES' && (!payload || Array.isArray(payload))) {
    return filterAllergySuggestions(map, payload, activeSe);
  }

  if (category !== 'ALLERGIES' && !Array.isArray(payload)) {
    return filterMhoCategorySuggestions(map, category, payload, activeSe);
  }

  throw new Error('Incompatible overload types');
}

function filterAllergySuggestions(
  map: MhoSuggestionsPerCategory,
  allergies: Allergy[] | undefined,
  activeSe?: SurgicalEvent
): ScratchSuggestion[] | undefined {
  if (activeSe && AllergyService.getInstance(activeSe.patient.id).isCleared()) {
    return undefined;
  }
  const existingSubstances = new Set(allergies?.map((al) => al.substanceCode));
  const suggestions = map['ALLERGIES']?.filter((s) => s.applies(activeSe));
  if (!existingSubstances.size) {
    return suggestions;
  }

  return suggestions?.filter((s) => {
    if (s.suggestionType === 'clear') {
      return s.sticky;
    }
    if (s.suggestionType === 'basic') {
      return !existingSubstances.has(s.code);
    }

    return true;
  });
}

function filterMhoCategorySuggestions(
  map: MhoSuggestionsPerCategory,
  category: MedicalHoItemCategory,
  medicalHo: MedicalHo | undefined,
  activeSe?: SurgicalEvent
): ScratchSuggestion[] | undefined {
  if (activeSe?.patient) {
    if (MedicalHoService.getInstance(activeSe.patient.id).isCleared(category)) {
      return undefined;
    }
  }
  const hasAnswer = createMhoAnswerCheck(getExistingMhoAnswers(medicalHo));
  const suggestions = map[category]?.filter((suggestion) =>
    suggestion.applies(activeSe)
  );
  const filtered = suggestions?.filter((suggestion) => {
    if (suggestion.suggestionType === 'clear') {
      return suggestion.sticky || !medicalHo?.[category]?.length;
    }
    if (suggestion.suggestionType === 'bi-choice') {
      return ![suggestion.leftChoice, suggestion.rightChoice].some((choice) =>
        hasAnswer({ ...choice, qcode: suggestion.qcode, category })
      );
    }
    if ('score' in suggestion) {
      return !hasAnswer({ qcode: suggestion.qcode, isScore: true });
    }
    return !hasAnswer({ ...suggestion, code: suggestion.code, category });
  });
  const hasNonClearSuggestions = filtered?.some(
    (suggestion) => suggestion.suggestionType !== 'clear'
  );
  const available = filtered?.filter(
    (suggestion) =>
      suggestion.suggestionType !== 'clear' ||
      hasNonClearSuggestions ||
      suggestion
        .toRequestBody(activeSe)
        .some((answer) => !hasAnswer({ category, ...answer })) ||
      suggestion.getScoresToClear(activeSe).some(
        (name) =>
          !hasAnswer({
            qcode: MedicalHoService.getQcodeByScoreName(name),
            isScore: true
          })
      )
  );
  return available?.length ? available : undefined;
}

function getExistingMhoAnswers(medicalHo: MedicalHo | undefined): MhoAnswer[] {
  return Object.values(MedicalHoItemCategory)
    .flatMap((category) => medicalHo?.[category] ?? [])
    .concat(medicalHo?.hiddenAnswers?.items ?? []);
}
